import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ToastProvider } from './contexts/ToastContext';
import { useAuth } from './contexts/AuthContext';
import ToastContainer from './components/common/ToastContainer';
import SessionWatcher from './components/common/SessionWatcher';
import Login from './components/Login';
import LoginHub from './components/LoginHub';
import SsoConsume from './components/SsoConsume';
import Dashboard from './components/Dashboard';
import MISDashboard from './components/MISDashboard';
import Aircraft from './components/Aircraft';
import Maintenance from './components/Maintenance';
import Store from './components/Store';
import ReceivingAndInspectionPage from './components/ReceivingAndInspectionPage';
import Loan from './components/Loan';
import Procurement from './components/Procurement';
import InventoryPage from './components/InventoryPage';
import ReturnsPage from './components/ReturnsPage';
import Issuance from './components/Issuance';
import Users from './components/Users';
import SalesEnquiry from './components/sales/SalesEnquiry';
import OperationsBoard from './components/ops/OperationsBoard';
import SectorDetailPage from './components/ops/SectorDetailPage';
import CrewDutyRoster from './components/ops/CrewDutyRoster';
import CrewInbox from './components/crew/CrewInbox';
import FlightLogEntryDetailPage from './components/crew/FlightLogEntryDetailPage';
import AuditLogs from './components/AuditLogs';
import Layout from './components/Layout';
import FeedbackList from './components/feedback/FeedbackList';
import FeedbackDetail from './components/feedback/FeedbackDetail';
import PublicFeedbackView from './components/feedback/PublicFeedbackView';
import OnboardingDashboard from './components/onboarding/OnboardingDashboard';
import OnboardingDetail from './components/onboarding/OnboardingDetail';
import OnboardingRequestForm from './components/onboarding/OnboardingRequestForm';
import MyProfile from './components/onboarding/MyProfile';
import ClientSettings from './components/ClientSettings';
import DgcaBaselineManagement from './components/ops/DgcaBaselineManagement';
import FieldMappingManagement from './components/pdf/FieldMappingManagement';
import TemplateTesting from './components/pdf/TemplateTesting';
import Glossary from './components/Glossary';
import FleetwrightTutorials from './components/FleetwrightTutorials';
import ReportCenter from './components/reportCenter/ReportCenter';
import TechnicalSupport from './components/TechnicalSupport';
import NoticeBoard from './components/NoticeBoard';
import { getPostLoginPath } from './constants/loginDepartments';
import RolesGlossary from './components/RolesGlossary';
import SpectControl from './components/SpectControl';
import TechnicalLogBookList from './components/camo/TechnicalLogBookList';
import TechnicalLogBookBackfill from './components/camo/TechnicalLogBookBackfill';
import TechnicalLogBookFormatManagement from './components/admin/TechnicalLogBookFormatManagement';
import CertificationAuthorizationPage from './components/certificationAuthorization/CertificationAuthorizationPage';
import NonCertifyingStaffAuthorizationPage from './components/nonCertifyingStaffAuthorization/NonCertifyingStaffAuthorizationPage';
import InstructorAuthorizationPage from './components/instructorAuthorization/InstructorAuthorizationPage';
import OneOffAuthorizationPage from './components/oneOffAuthorization/OneOffAuthorizationPage';
import EntityJsonViewerPage from './components/common/EntityJsonViewerPage';
import PartsCatalog from './components/PartsCatalog';
import MasterDataManagement from './components/MasterDataManagement';
import AircraftLifecycle from './components/AircraftLifecycle';
import SerializedItemLifecycle from './components/SerializedItemLifecycle/SerializedItemLifecycle';
import AMPSchedules from './components/AMPSchedules';
import ADSBServiceBulletins from './components/ADSBServiceBulletins';
import Planning from './components/Planning';
import MroBoard from './components/MroBoard';
import MroBoardLab from './components/MroBoardLab';
import MroLabPlanningPage from './components/mroLab/MroLabPlanningPage';
import MroLabManagerPage from './components/mroLab/MroLabManagerPage';
import MroLabEngineerPage from './components/mroLab/MroLabEngineerPage';
import MroLabQmPage from './components/mroLab/MroLabQmPage';
import QuickAdd from './components/QuickAdd';
import AccountableManagerDashboard from './components/AccountableManagerDashboard';
import AmMroDashboard from './components/AmMroDashboard';
import CamoTeamDashboard from './components/camo/CamoTeamDashboard';
import OpsTeamDashboard from './components/ops/OpsTeamDashboard';
import ClientPortal from './components/ClientPortal';
import OperatorPortal from './components/OperatorPortal';
import HrmsSuite from './components/hrms/HrmsSuite';
import StaffTrainingPage from './components/hrms/StaffTrainingPage';
import { ROLES } from './constants/roles';
import { roleMatches } from './utils/rolePermissions';
import { hasGrantModuleAccess } from './utils/viewPermissions';
import AxiosInterceptor from './components/common/AxiosInterceptor';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  components: {
    MuiTable: {
      defaultProps: {
        stickyHeader: true,
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme: muiTheme }) => ({
          backgroundColor: muiTheme.palette.background.paper,
          zIndex: 2,
        }),
      },
    },
    MuiListSubheader: {
      defaultProps: {
        disableSticky: false,
      },
      styleOverrides: {
        root: ({ theme: muiTheme }) => ({
          backgroundColor: muiTheme.palette.background.paper,
          zIndex: 1,
        }),
      },
    },
  },
});

const fetchSignatureByIdUrl = (id) => `/api/signatures/${id}`;
const fetchMediaByIdUrl = (id) => `/api/media/${id}`;

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
}

function OnboardingDashboardGuard() {
  const { user, viewGrants } = useAuth();
  
  if (roleMatches(user?.role, [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.HR_MANAGER])
    || hasGrantModuleAccess(viewGrants, 'users-onboard')) {
    return <OnboardingDashboard />;
  }
  
  return <Navigate to="/" />;
}

function SuperAdminGuard({ children }) {
  const { user } = useAuth();
  if (roleMatches(user?.role, [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER])) {
    return children;
  }
  return <Navigate to="/" />;
}

function SpectatorGuard({ children }) {
  const { user } = useAuth();
  if (user?.role === ROLES.SPECTATOR) {
    return children;
  }
  return <Navigate to="/" />;
}

function RoleOrGrantGuard({ moduleKey, allowRoles = [], children }) {
  const { user, viewGrants } = useAuth();
  const roleAllowed = allowRoles.length === 0 || roleMatches(user?.role, allowRoles);
  if (roleAllowed || (moduleKey && hasGrantModuleAccess(viewGrants, moduleKey))) {
    return children;
  }
  return <Navigate to="/" />;
}

function PlanningPageGuard({ children }) {
  return (
    <RoleOrGrantGuard
      moduleKey="planning"
      allowRoles={[ROLES.PLANNING_MANAGER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER]}
    >
      {children}
    </RoleOrGrantGuard>
  );
}

function MroBoardPageGuard({ children }) {
  return (
    <RoleOrGrantGuard
      moduleKey="mro"
      allowRoles={[ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER]}
    >
      {children}
    </RoleOrGrantGuard>
  );
}

function AccountableManagerDashboardGuard({ children }) {
  return (
    <RoleOrGrantGuard
      moduleKey="am-dashboard"
      allowRoles={[ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN]}
    >
      {children}
    </RoleOrGrantGuard>
  );
}

function AmMroDashboardGuard({ children }) {
  return (
    <RoleOrGrantGuard
      moduleKey="am-mro-dashboard"
      allowRoles={[ROLES.AM_MRO, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER]}
    >
      {children}
    </RoleOrGrantGuard>
  );
}

function SalesEnquiryGuard({ children }) {
  return (
    <RoleOrGrantGuard
      moduleKey="sales"
      allowRoles={[
        ROLES.SALES,
        ROLES.OPERATIONS_MANAGER,
        ROLES.OPERATIONS_EXECUTIVE,
        ROLES.ACCOUNTABLE_MANAGER,
        ROLES.SUPER_ADMIN
      ]}
    >
      {children}
    </RoleOrGrantGuard>
  );
}

function CamoDashboardGuard({ children }) {
  return (
    <RoleOrGrantGuard
      moduleKey="camo-dashboard"
      allowRoles={[
        ROLES.CA_MANAGER,
        ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER,
        ROLES.CAMO_QM_MANAGER,
        ROLES.CAMO_QM_OFFICER,
        ROLES.SUPER_ADMIN,
        ROLES.ACCOUNTABLE_MANAGER
      ]}
    >
      {children}
    </RoleOrGrantGuard>
  );
}

function OpsDashboardGuard({ children }) {
  return (
    <RoleOrGrantGuard
      moduleKey="ops-dashboard"
      allowRoles={[ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN]}
    >
      {children}
    </RoleOrGrantGuard>
  );
}

function ClientPortalGuard({ children }) {
  const { user } = useAuth();
  if ([ROLES.CLIENT, ROLES.SPECTATOR].includes(user?.role)) {
    return children;
  }
  return <Navigate to="/" />;
}

function OperatorPortalGuard({ children }) {
  const { user } = useAuth();
  if (user?.role === ROLES.EXTERNAL_OPERATOR) {
    return children;
  }
  return <Navigate to="/" />;
}

function AppRoutes() {
  const { user } = useAuth();
  const isCamoDashboardRole = [
    ROLES.CA_MANAGER,
    ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER,
    ROLES.CAMO_QM_MANAGER,
    ROLES.CAMO_QM_OFFICER
  ].includes(user?.role);
  if (user?.role === ROLES.CLIENT) {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/client-portal" />} />
        <Route path="/login/:department" element={<Navigate to="/client-portal" />} />
        <Route path="/client-portal" element={
          <ProtectedRoute>
            <ClientPortalGuard>
              <Layout>
                <ClientPortal />
              </Layout>
            </ClientPortalGuard>
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/client-portal" replace />} />
      </Routes>
    );
  }

  if (user?.role === ROLES.EXTERNAL_OPERATOR) {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/operator-portal" />} />
        <Route path="/login/:department" element={<Navigate to="/operator-portal" />} />
        <Route path="/operator-portal" element={
          <ProtectedRoute>
            <OperatorPortalGuard>
              <Layout>
                <OperatorPortal />
              </Layout>
            </OperatorPortalGuard>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Layout>
              <MyProfile />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/operator-portal" replace />} />
      </Routes>
    );
  }

  if (user?.role === ROLES.EMPLOYEE) {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/notice-board" />} />
        <Route path="/login/:department" element={<Navigate to="/notice-board" />} />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Layout>
              <MyProfile />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/notice-board" element={
          <ProtectedRoute>
            <Layout>
              <NoticeBoard />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/users/onboard/:id" element={
          <ProtectedRoute>
            <Layout>
              <OnboardingDetail />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/notice-board" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getPostLoginPath(user)} /> : <LoginHub />} />
      <Route path="/login/:department" element={user ? <Navigate to={getPostLoginPath(user)} /> : <Login />} />
      <Route path="/sso/consume" element={<SsoConsume />} />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout>
            {user?.role === ROLES.ACCOUNTABLE_MANAGER
              ? <AccountableManagerDashboard />
              : user?.role === ROLES.AM_MRO
                ? <AmMroDashboard />
                : isCamoDashboardRole
                  ? <CamoTeamDashboard />
                  : <Dashboard />}
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/camo-dashboard" element={
        <ProtectedRoute>
          <CamoDashboardGuard>
            <Layout>
              <CamoTeamDashboard />
            </Layout>
          </CamoDashboardGuard>
        </ProtectedRoute>
      } />
      <Route path="/am-dashboard" element={
        <ProtectedRoute>
          <AccountableManagerDashboardGuard>
            <Layout>
              <AccountableManagerDashboard />
            </Layout>
          </AccountableManagerDashboardGuard>
        </ProtectedRoute>
      } />
      <Route path="/am-mro-dashboard" element={
        <ProtectedRoute>
          <AmMroDashboardGuard>
            <Layout>
              <AmMroDashboard />
            </Layout>
          </AmMroDashboardGuard>
        </ProtectedRoute>
      } />
      <Route path="/ops-dashboard" element={
        <ProtectedRoute>
          <OpsDashboardGuard>
            <Layout>
              <OpsTeamDashboard />
            </Layout>
          </OpsDashboardGuard>
        </ProtectedRoute>
      } />
      <Route path="/client-portal" element={
        <ProtectedRoute>
          <ClientPortalGuard>
            <Layout>
              <ClientPortal />
            </Layout>
          </ClientPortalGuard>
        </ProtectedRoute>
      } />
      <Route path="/aircraft" element={
        <ProtectedRoute>
          <Layout>
            <Aircraft />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/aircraft-lifecycle" element={
        <ProtectedRoute>
          <Layout>
            <AircraftLifecycle />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/serialized-items" element={
        <ProtectedRoute>
          <Layout>
            <SerializedItemLifecycle />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/parts-catalog" element={
        <ProtectedRoute>
          <Layout>
            <PartsCatalog />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/master-data" element={
        <ProtectedRoute>
          <Layout>
            <MasterDataManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/maintenance" element={
        <ProtectedRoute>
          <Layout>
            <Maintenance />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/mro" element={
        <ProtectedRoute>
          <MroBoardPageGuard>
            <Layout>
              <MroBoard />
            </Layout>
          </MroBoardPageGuard>
        </ProtectedRoute>
      } />
      <Route path="/mro/lab" element={
        <ProtectedRoute>
          <SpectatorGuard>
            <Layout>
              <MroBoardLab />
            </Layout>
          </SpectatorGuard>
        </ProtectedRoute>
      } />
      <Route path="/mro/lab/planning" element={
        <ProtectedRoute>
          <SpectatorGuard>
            <Layout>
              <MroLabPlanningPage />
            </Layout>
          </SpectatorGuard>
        </ProtectedRoute>
      } />
      <Route path="/mro/lab/manager" element={
        <ProtectedRoute>
          <SpectatorGuard>
            <Layout>
              <MroLabManagerPage />
            </Layout>
          </SpectatorGuard>
        </ProtectedRoute>
      } />
      <Route path="/mro/lab/engineer" element={
        <ProtectedRoute>
          <SpectatorGuard>
            <Layout>
              <MroLabEngineerPage />
            </Layout>
          </SpectatorGuard>
        </ProtectedRoute>
      } />
      <Route path="/mro/lab/qm" element={
        <ProtectedRoute>
          <SpectatorGuard>
            <Layout>
              <MroLabQmPage />
            </Layout>
          </SpectatorGuard>
        </ProtectedRoute>
      } />
      <Route path="/planning" element={
        <ProtectedRoute>
          <PlanningPageGuard>
            <Layout>
              <Planning />
            </Layout>
          </PlanningPageGuard>
        </ProtectedRoute>
      } />
      <Route path="/amp-schedules" element={
        <ProtectedRoute>
          <Layout>
            <AMPSchedules />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/adsb-service-bulletin" element={
        <ProtectedRoute>
          <Layout>
            <ADSBServiceBulletins />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/store" element={
        <ProtectedRoute>
          <Layout>
            <Store />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/store/inventory" element={
        <ProtectedRoute>
          <Layout>
            <InventoryPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/store/returns" element={
        <ProtectedRoute>
          <Layout>
            <ReturnsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/store/receiving" element={
        <ProtectedRoute>
          <Layout>
            <Store />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/store/receving" element={
        <ProtectedRoute>
          <Layout>
            <Store />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/receiving-and-inspection" element={
        <ProtectedRoute>
          <Layout>
            <ReceivingAndInspectionPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/loan" element={
        <ProtectedRoute>
          <Layout>
            <Loan />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/procurement" element={
        <ProtectedRoute>
          <Layout>
            <Procurement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/issuance" element={
        <ProtectedRoute>
          <Layout>
            <Issuance />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute>
          <Layout>
            <Users />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/client-settings" element={
        <ProtectedRoute>
          <Layout>
            <ClientSettings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/dgca-baseline" element={
        <ProtectedRoute>
          <Layout>
            <DgcaBaselineManagement />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/sales" element={
        <ProtectedRoute>
          <SalesEnquiryGuard>
            <Layout>
              <SalesEnquiry />
            </Layout>
          </SalesEnquiryGuard>
        </ProtectedRoute>
      } />
      <Route path="/ops" element={
        <ProtectedRoute>
          <Layout>
            <OperationsBoard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/ops/sectors/:id" element={
        <ProtectedRoute>
          <Layout>
            <SectorDetailPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/crew-duty-roster" element={
        <ProtectedRoute>
          <Layout>
            <CrewDutyRoster />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/hrms" element={
        <ProtectedRoute>
          <Layout>
            <HrmsSuite />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/staff-training" element={
        <ProtectedRoute>
          <Layout>
            <StaffTrainingPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/crew" element={
        <ProtectedRoute>
          <Layout>
            <CrewInbox />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/flight-log/:id" element={
        <ProtectedRoute>
          <Layout>
            <FlightLogEntryDetailPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/audit" element={
        <ProtectedRoute>
          <Layout>
            <AuditLogs />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/users/onboard" element={
        <ProtectedRoute>
          <Layout>
            <OnboardingDashboardGuard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/users/onboard/new" element={
        <ProtectedRoute>
          <Layout>
            <OnboardingRequestForm />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/users/onboard/:id" element={
        <ProtectedRoute>
          <Layout>
            <OnboardingDetail />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <MyProfile />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/feedback" element={
        <ProtectedRoute>
          <Layout>
            <FeedbackList />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/feedback/:id" element={
        <ProtectedRoute>
          <Layout>
            <FeedbackDetail />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/feedback/public/:token" element={<PublicFeedbackView />} />
      <Route path="/mis-dashboard" element={
        <ProtectedRoute>
          <Layout>
            <MISDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/pdf/field-mappings" element={
        <ProtectedRoute>
          <SpectatorGuard>
            <Layout>
              <FieldMappingManagement />
            </Layout>
          </SpectatorGuard>
        </ProtectedRoute>
      } />
      <Route path="/pdf/template-testing" element={
        <ProtectedRoute>
          <SpectatorGuard>
            <Layout>
              <TemplateTesting />
            </Layout>
          </SpectatorGuard>
        </ProtectedRoute>
      } />
      <Route path="/glossary" element={
        <ProtectedRoute>
          <Layout>
            <Glossary />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/fleetwright-tutorials" element={
        <ProtectedRoute>
          <Layout>
            <FleetwrightTutorials />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/report-center" element={
        <ProtectedRoute>
          <RoleOrGrantGuard moduleKey="report-center" allowRoles={[ROLES.SPECTATOR, ROLES.SUPER_ADMIN, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.STORE_INSPECTOR, ROLES.SAFETY_COFS, ROLES.SAFETY_MANAGER, ROLES.SAFETY_OFFICER]}>
            <Layout>
              <ReportCenter />
            </Layout>
          </RoleOrGrantGuard>
        </ProtectedRoute>
      } />
      <Route path="/report-center/:reportType" element={
        <ProtectedRoute>
          <RoleOrGrantGuard moduleKey="report-center" allowRoles={[ROLES.SPECTATOR, ROLES.SUPER_ADMIN, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.STORE_INSPECTOR, ROLES.SAFETY_COFS, ROLES.SAFETY_MANAGER, ROLES.SAFETY_OFFICER]}>
            <Layout>
              <ReportCenter />
            </Layout>
          </RoleOrGrantGuard>
        </ProtectedRoute>
      } />
      <Route path="/technical-support" element={
        <ProtectedRoute>
          <Layout>
            <TechnicalSupport />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/notice-board" element={
        <ProtectedRoute>
          <Layout>
            <NoticeBoard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/roles-glossary" element={
        <ProtectedRoute>
          <RoleOrGrantGuard
            moduleKey="roles-glossary"
            allowRoles={[ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER]}
          >
            <Layout>
              <RolesGlossary />
            </Layout>
          </RoleOrGrantGuard>
        </ProtectedRoute>
      } />
      <Route path="/spect-control" element={
        <ProtectedRoute>
          <SpectatorGuard>
            <Layout>
              <SpectControl />
            </Layout>
          </SpectatorGuard>
        </ProtectedRoute>
      } />
      <Route path="/quick-add" element={
        <ProtectedRoute>
          <Layout>
            <QuickAdd />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/authorization" element={
        <ProtectedRoute>
          <Layout>
            <CertificationAuthorizationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/certification-authorization" element={
        <ProtectedRoute>
          <Layout>
            <CertificationAuthorizationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/authorization/non-certifying" element={
        <ProtectedRoute>
          <Layout>
            <NonCertifyingStaffAuthorizationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/non-certifying-staff-authorization" element={
        <ProtectedRoute>
          <Layout>
            <NonCertifyingStaffAuthorizationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/authorization/instructor" element={
        <ProtectedRoute>
          <Layout>
            <InstructorAuthorizationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/instructor-authorization" element={
        <ProtectedRoute>
          <Layout>
            <InstructorAuthorizationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/authorization/one-off" element={
        <ProtectedRoute>
          <Layout>
            <OneOffAuthorizationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/one-off-authorization" element={
        <ProtectedRoute>
          <Layout>
            <OneOffAuthorizationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/technical-log-book" element={
        <ProtectedRoute>
          <Layout>
            <TechnicalLogBookList />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/technical-log-book/backfill" element={
        <ProtectedRoute>
          <Layout>
            <TechnicalLogBookBackfill />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/signatures/:id" element={
        <ProtectedRoute>
          <Layout>
            <EntityJsonViewerPage title="Signature" fetchUrl={fetchSignatureByIdUrl} />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/media/:id" element={
        <ProtectedRoute>
          <Layout>
            <EntityJsonViewerPage title="Media" fetchUrl={fetchMediaByIdUrl} />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/technical-log-book-format" element={
        <ProtectedRoute>
          <Layout>
            <TechnicalLogBookFormatManagement />
          </Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <ToastProvider>
            <AxiosInterceptor>
              <SessionWatcher />
              <Router>
                <AppRoutes />
                <ToastContainer />
              </Router>
            </AxiosInterceptor>
          </ToastProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;